import { createReducer, on } from '@ngrx/store';
import { FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE } from '../../../constants/fire-sight.constants';
import {
  IFireSightHistoryDateFilter,
  IFireSightIncidentSummary,
  IFireSightSimulation,
  TFireSightActiveView,
  TFireSightWebSocketStatus,
} from '../../../interfaces/fire-sight/IFireSightModels';
import { IFireSightState } from '../../../interfaces/fire-sight/IFireSightState';
import * as FireSightActions from '../../actions/fire-sight/fire-sight.actions';

export const FIRE_SIGHT_FEATURE_KEY = 'fireSight';

export const initialFireSightState: IFireSightState = {
  projectUuid: null,
  isActive: false,
  bootstrap: null,
  monitoringAreas: [],
  incidents: [],
  historicalIncidents: [],
  historicalIncidentsTotal: 0,
  historicalIncidentsNextCursor: null,
  historicalIncidentsHasMore: false,
  historyQueryKey: 'all',
  incidentDetail: null,
  observations: [],
  simulations: [],
  selectedIncidentUuid: null,
  selectedObservationUuid: null,
  selectedSimulationUuid: null,
  selectedHorizonH: null,
  selectedProductCode: null,
  activeView: 'starting',
  overviewView: 'live',
  historyDateFilter: null,
  historyDisplayNameFilter: '',
  historyCentroidsVisible: false,
  websocketStatus: 'disconnected',
  loading: {
    bootstrap: false,
    monitoringAreas: false,
    history: false,
    historyMore: false,
    incidentContext: false,
    simulation: false,
  },
  errors: {
    bootstrap: null,
    monitoringAreas: null,
    history: null,
    incidentContext: null,
    simulation: null,
    websocket: null,
  },
};

function upsertIncident(
  incidents: IFireSightIncidentSummary[],
  incident: IFireSightIncidentSummary,
): IFireSightIncidentSummary[] {
  return incidents.some((item) => item.incident_uuid === incident.incident_uuid)
    ? incidents.map((item) =>
        item.incident_uuid === incident.incident_uuid ? incident : item,
      )
    : [incident, ...incidents];
}

function normalizeHistoryDisplayNameFilter(displayName: string): string {
  return displayName.trim();
}

function historyQueryKey(
  dateFilter: IFireSightHistoryDateFilter | null,
  displayNameFilter: string,
): string {
  const normalizedDisplayName = normalizeHistoryDisplayNameFilter(
    displayNameFilter,
  );
  if (!dateFilter && !normalizedDisplayName) {
    return 'all';
  }
  return JSON.stringify({
    date: dateFilter
      ? [dateFilter.year, dateFilter.month, dateFilter.day].join('-')
      : null,
    displayName: normalizedDisplayName.toLowerCase(),
  });
}

function matchesHistoryFilters(
  incident: IFireSightIncidentSummary,
  dateFilter: IFireSightHistoryDateFilter | null,
  displayNameFilter: string,
): boolean {
  if (dateFilter) {
    const lastObservedAt = new Date(incident.last_observed_at);
    const matchesDate =
      lastObservedAt.getFullYear() === dateFilter.year &&
      lastObservedAt.getMonth() + 1 === dateFilter.month &&
      lastObservedAt.getDate() === dateFilter.day;
    if (!matchesDate) {
      return false;
    }
  }

  const normalizedDisplayName = normalizeHistoryDisplayNameFilter(
    displayNameFilter,
  ).toLowerCase();
  if (!normalizedDisplayName) {
    return true;
  }
  return (
    incident.display_name
      ?.toLowerCase()
      .includes(normalizedDisplayName) ?? false
  );
}

function mergeHistoricalIncidents(
  current: IFireSightIncidentSummary[],
  incoming: IFireSightIncidentSummary[],
): IFireSightIncidentSummary[] {
  const incidentsByUuid = new Map(
    current.map((incident) => [incident.incident_uuid, incident]),
  );
  incoming.forEach((incident) =>
    incidentsByUuid.set(incident.incident_uuid, incident),
  );
  return Array.from(incidentsByUuid.values()).sort((left, right) => {
    const dateDifference =
      Date.parse(right.last_observed_at) - Date.parse(left.last_observed_at);
    return dateDifference !== 0
      ? dateDifference
      : right.incident_uuid.localeCompare(left.incident_uuid);
  });
}

function upsertSimulation(
  simulations: IFireSightSimulation[],
  simulation: IFireSightSimulation,
): IFireSightSimulation[] {
  const index = simulations.findIndex((item) => item.uuid === simulation.uuid);
  if (index < 0) {
    return [simulation, ...simulations];
  }
  return simulations.map((item) =>
    item.uuid === simulation.uuid ? simulation : item,
  );
}

export const fireSightReducer = createReducer(
  initialFireSightState,
  on(FireSightActions.enterFireSight, (state, { projectUuid }) => {
    if (state.projectUuid === projectUuid) {
      return {
        ...state,
        isActive: true,
        errors: { ...state.errors, websocket: null },
      };
    }
    return {
      ...initialFireSightState,
      projectUuid,
      isActive: true,
    };
  }),
  on(FireSightActions.leaveFireSight, (state) => ({
    ...state,
    isActive: false,
    websocketStatus: 'disconnected' as TFireSightWebSocketStatus,
  })),

  on(FireSightActions.loadFireSightBootstrap, (state) => ({
    ...state,
    loading: { ...state.loading, bootstrap: true },
    errors: { ...state.errors, bootstrap: null },
  })),
  on(FireSightActions.loadFireSightBootstrapSuccess, (state, { bootstrap }) => ({
    ...state,
    bootstrap,
    incidents: bootstrap.active_incidents,
    loading: { ...state.loading, bootstrap: false },
  })),
  on(FireSightActions.loadFireSightBootstrapFailure, (state, { error }) => ({
    ...state,
    loading: { ...state.loading, bootstrap: false },
    errors: { ...state.errors, bootstrap: error },
  })),

  on(FireSightActions.loadFireSightMonitoringAreas, (state) => ({
    ...state,
    loading: { ...state.loading, monitoringAreas: true },
    errors: { ...state.errors, monitoringAreas: null },
  })),
  on(
    FireSightActions.loadFireSightMonitoringAreasSuccess,
    (state, { monitoringAreas }) => ({
      ...state,
      monitoringAreas,
      loading: { ...state.loading, monitoringAreas: false },
    }),
  ),
  on(
    FireSightActions.loadFireSightMonitoringAreasFailure,
    (state, { error }) => ({
      ...state,
      loading: { ...state.loading, monitoringAreas: false },
      errors: { ...state.errors, monitoringAreas: error },
    }),
  ),

  on(FireSightActions.setFireSightOverviewView, (state, { overviewView }) => ({
    ...state,
    overviewView,
  })),
  on(
    FireSightActions.setFireSightHistoryDateFilter,
    (state, { dateFilter }) => ({
      ...state,
      historyDateFilter: dateFilter,
    }),
  ),
  on(
    FireSightActions.setFireSightHistoryDisplayNameFilter,
    (state, { displayName }) => ({
      ...state,
      historyDisplayNameFilter:
        normalizeHistoryDisplayNameFilter(displayName),
    }),
  ),
  on(
    FireSightActions.setFireSightHistoryCentroidsVisible,
    (state, { visible }) => ({
      ...state,
      historyCentroidsVisible: visible,
    }),
  ),
  on(FireSightActions.loadFireSightHistoricalIncidents, (state) => ({
    ...state,
    historicalIncidents: [],
    historicalIncidentsTotal: 0,
    historicalIncidentsNextCursor: null,
    historicalIncidentsHasMore: false,
    historyQueryKey: historyQueryKey(
      state.historyDateFilter,
      state.historyDisplayNameFilter,
    ),
    loading: { ...state.loading, history: true, historyMore: false },
    errors: { ...state.errors, history: null },
  })),
  on(
    FireSightActions.loadFireSightHistoricalIncidentsSuccess,
    (state, { incidents, total, nextCursor, hasMore, queryKey }) =>
      state.historyQueryKey !== queryKey
        ? state
        : {
            ...state,
            historicalIncidents: incidents,
            historicalIncidentsTotal: total,
            historicalIncidentsNextCursor: nextCursor,
            historicalIncidentsHasMore: hasMore,
            loading: { ...state.loading, history: false },
          },
  ),
  on(
    FireSightActions.loadFireSightHistoricalIncidentsFailure,
    (state, { error, queryKey }) =>
      state.historyQueryKey !== queryKey
        ? state
        : {
            ...state,
            loading: { ...state.loading, history: false },
            errors: { ...state.errors, history: error },
          },
  ),
  on(FireSightActions.loadMoreFireSightHistoricalIncidents, (state) =>
    !state.historicalIncidentsHasMore ||
    !state.historicalIncidentsNextCursor ||
    state.loading.history
      ? state
      : {
          ...state,
          loading: { ...state.loading, historyMore: true },
          errors: { ...state.errors, history: null },
        },
  ),
  on(
    FireSightActions.loadMoreFireSightHistoricalIncidentsSuccess,
    (
      state,
      { incidents, total, nextCursor, hasMore, queryKey, requestedCursor },
    ) =>
      state.historyQueryKey !== queryKey ||
      state.historicalIncidentsNextCursor !== requestedCursor
        ? state
        : {
            ...state,
            historicalIncidents: mergeHistoricalIncidents(
              state.historicalIncidents,
              incidents,
            ),
            historicalIncidentsTotal: total,
            historicalIncidentsNextCursor: nextCursor,
            historicalIncidentsHasMore: hasMore,
            loading: { ...state.loading, historyMore: false },
          },
  ),
  on(
    FireSightActions.loadMoreFireSightHistoricalIncidentsFailure,
    (state, { error, queryKey, requestedCursor }) =>
      state.historyQueryKey !== queryKey ||
      state.historicalIncidentsNextCursor !== requestedCursor
        ? state
        : {
            ...state,
            loading: { ...state.loading, historyMore: false },
            errors: { ...state.errors, history: error },
          },
  ),

  on(FireSightActions.selectFireSightIncident, (state, { incidentUuid }) => ({
    ...state,
    selectedIncidentUuid: incidentUuid,
    selectedObservationUuid: null,
    selectedSimulationUuid: null,
    selectedHorizonH: null,
    selectedProductCode: null,
    incidentDetail: null,
    observations: [],
    simulations: [],
    activeView: 'monitoring' as TFireSightActiveView,
    loading: { ...state.loading, incidentContext: true },
    errors: { ...state.errors, incidentContext: null },
  })),
  on(FireSightActions.loadFireSightIncidentContext, (state) => ({
    ...state,
    loading: { ...state.loading, incidentContext: true },
    errors: { ...state.errors, incidentContext: null },
  })),
  on(
    FireSightActions.loadFireSightIncidentContextSuccess,
    (state, { incident, observations, simulations }) => {
      const selectedSimulation = simulations[0] ?? null;
      return {
        ...state,
        incidentDetail: incident,
        observations,
        simulations,
        selectedIncidentUuid: incident.incident_uuid,
        selectedObservationUuid: observations[0]?.uuid ?? null,
        selectedSimulationUuid: selectedSimulation?.uuid ?? null,
        selectedHorizonH: selectedSimulation?.horizons[0]?.horizon_h ?? null,
        selectedProductCode: selectedSimulation
          ? FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE
          : null,
        loading: { ...state.loading, incidentContext: false },
      };
    },
  ),
  on(FireSightActions.loadFireSightIncidentContextFailure, (state, { error }) => ({
    ...state,
    loading: { ...state.loading, incidentContext: false },
    errors: { ...state.errors, incidentContext: error },
  })),

  on(FireSightActions.returnToFireSightOverview, (state) => ({
    ...state,
    activeView: 'starting' as TFireSightActiveView,
    selectedIncidentUuid: null,
    selectedObservationUuid: null,
    selectedSimulationUuid: null,
    selectedHorizonH: null,
    selectedProductCode: null,
    incidentDetail: null,
    observations: [],
    simulations: [],
  })),
  on(FireSightActions.setFireSightActiveView, (state, { activeView }) => ({
    ...state,
    activeView:
      activeView === 'simulation' as TFireSightActiveView && !state.selectedSimulationUuid
        ? 'monitoring' as TFireSightActiveView
        : activeView,
  })),
  on(FireSightActions.selectFireSightObservation, (state, { observationUuid }) => ({
    ...state,
    selectedObservationUuid: observationUuid,
  })),
  on(FireSightActions.selectFireSightSimulation, (state, { simulationUuid }) => {
    const simulation = state.simulations.find((item) => item.uuid === simulationUuid);
    return {
      ...state,
      selectedSimulationUuid: simulationUuid,
      selectedHorizonH: simulation?.horizons[0]?.horizon_h ?? null,
      selectedProductCode: FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE,
    };
  }),
  on(FireSightActions.selectFireSightHorizon, (state, { horizonH }) => ({
    ...state,
    selectedHorizonH: horizonH,
  })),
  on(FireSightActions.selectFireSightProduct, (state, { productCode }) => ({
    ...state,
    selectedProductCode: productCode,
  })),

  on(FireSightActions.startFireSightSimulation, (state) => ({
    ...state,
    loading: { ...state.loading, simulation: true },
    errors: { ...state.errors, simulation: null },
  })),
  on(
    FireSightActions.startFireSightSimulationSuccess,
    (state, { simulation }) => ({
      ...state,
      simulations: upsertSimulation(state.simulations, simulation),
      selectedSimulationUuid: simulation.uuid,
      selectedHorizonH: simulation.horizons[0]?.horizon_h ?? null,
      selectedProductCode: FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE,
      activeView: 'simulation' as TFireSightActiveView,
      loading: { ...state.loading, simulation: false },
    }),
  ),
  on(FireSightActions.startFireSightSimulationFailure, (state, { error }) => ({
    ...state,
    loading: { ...state.loading, simulation: false },
    errors: { ...state.errors, simulation: error },
  })),

  on(FireSightActions.loadFireSightSimulation, (state) => ({
    ...state,
    loading: { ...state.loading, simulation: true },
  })),
  on(FireSightActions.loadFireSightSimulationSuccess, (state, { simulation }) => ({
    ...state,
    simulations: upsertSimulation(state.simulations, simulation),
    selectedHorizonH:
      state.selectedSimulationUuid === simulation.uuid &&
      state.selectedHorizonH === null
        ? (simulation.horizons[0]?.horizon_h ?? null)
        : state.selectedHorizonH,
    selectedProductCode:
      state.selectedSimulationUuid === simulation.uuid &&
      state.selectedProductCode === null
        ? FIRE_SIGHT_DEFAULT_SIMULATION_PRODUCT_CODE
        : state.selectedProductCode,
    loading: { ...state.loading, simulation: false },
    errors: { ...state.errors, simulation: simulation.error_message ?? null },
  })),
  on(FireSightActions.loadFireSightSimulationFailure, (state, { error }) => ({
    ...state,
    loading: { ...state.loading, simulation: false },
    errors: { ...state.errors, simulation: error },
  })),

  on(FireSightActions.fireSightWebSocketStatusChanged, (state, { change }) => ({
    ...state,
    websocketStatus: change.status,
    errors: {
      ...state.errors,
      websocket:
        change.status === 'error' ? 'FireSight real-time connection failed.' : null,
    },
  })),
  on(FireSightActions.upsertFireSightIncident, (state, { incident }) => {
    const historicalIndex = state.historicalIncidents.findIndex(
      (item) => item.incident_uuid === incident.incident_uuid,
    );
    const belongsToCurrentHistory =
      incident.engine_status === 'extinct' &&
      matchesHistoryFilters(
        incident,
        state.historyDateFilter,
        state.historyDisplayNameFilter,
      );
    const historicalIncidents = belongsToCurrentHistory
      ? mergeHistoricalIncidents(state.historicalIncidents, [incident])
      : state.historicalIncidents.filter(
          (item) => item.incident_uuid !== incident.incident_uuid,
        );

    return {
      ...state,
      incidents:
        incident.engine_status === 'extinct'
          ? state.incidents.filter(
              (item) => item.incident_uuid !== incident.incident_uuid,
            )
          : upsertIncident(state.incidents, incident),
      historicalIncidents,
      historicalIncidentsTotal:
        belongsToCurrentHistory && historicalIndex < 0
          ? state.historicalIncidentsTotal + 1
          : !belongsToCurrentHistory && historicalIndex >= 0
            ? Math.max(0, state.historicalIncidentsTotal - 1)
            : state.historicalIncidentsTotal,
    };
  }),
  on(
    FireSightActions.patchFireSightIncidentOperationalStatus,
    (state, { incidentUuid, operationalStatus }) => ({
      ...state,
      incidents: state.incidents.map((incident) =>
        incident.incident_uuid === incidentUuid
          ? { ...incident, operational_status: operationalStatus }
          : incident,
      ),
      historicalIncidents: state.historicalIncidents.map((incident) =>
        incident.incident_uuid === incidentUuid
          ? { ...incident, operational_status: operationalStatus }
          : incident,
      ),
      incidentDetail:
        state.incidentDetail?.incident_uuid === incidentUuid
          ? { ...state.incidentDetail, operational_status: operationalStatus }
          : state.incidentDetail,
    }),
  ),
);
